import React from 'react';
import {
  FieldArrayWithId,
  FieldErrors,
  FieldValues,
  FormState,
  UseFieldArrayAppend,
  UseFieldArrayInsert,
  UseFieldArrayMove,
  UseFieldArrayRemove,
  UseFieldArrayReplace,
  UseFieldArrayUpdate,
  UseFormHandleSubmit,
  UseFormSetValue,
} from 'react-hook-form';
import { FieldItem, Value, Values } from '@lib/interfaces/form';
import { GraphQLFormattedError } from 'graphql';
import { Steps } from '@lib/enums/form';
import { DroppableStateSnapshot } from '@hello-pangea/dnd';

export enum ErrorsKey {
  General = 'generalErrors',
}

export interface ActionsProps {
  errors: FieldErrors;
  isDirty: boolean;
  loading: boolean;
  onSubmitForm: (
    e?: React.BaseSyntheticEvent<object, any, any> | undefined,
  ) => Promise<void>;
  onCancel?: () => void;
}

export interface FormRef {
  errors: FieldErrors;
  reset: (values?: Values, options?: object) => void;
  setValue: UseFormSetValue<FieldValues>;
  values: Values;
  isDirty: boolean;
}

export type FormRefType = React.RefObject<FormRef | undefined>;

export interface SourceFields {
  questions?: { id: string }[];
}

export type SourceAppendValues = {
  [key: string]: any;
};

export type RenderAfterForm = () => React.ReactNode;

export interface FormContainerProps {
  actions?: (props: ActionsProps) => React.ReactElement;
  actionsComponent?: React.JSX.ElementType;
  actionsComponentProps?: unknown;
  defaultValues: Values;
  disableAwayConfirmation?: boolean;
  fields: FieldItem[];
  formClassName?: string;
  formId?: string;
  formItemsClassName?: string;
  formRef?: React.RefObject<FormRef | undefined>;
  graphQLErrors?: readonly GraphQLFormattedError[];
  graphQLErrorsReplacePathMap?: { [key: string]: string };
  graphQLErrorsSkipPath?: string[];
  loading?: boolean;
  loadingDesc?: string;
  onSubmit?: (values: Values) => void;
  persistForm?: boolean;
  persistFormSeconds?: number;
  reValidateMode?: 'onBlur' | 'onChange' | 'onSubmit' | undefined;
  renderAfterForm?: RenderAfterForm;
  sourceAppendValues?: SourceAppendValues;
  sourceFields?: SourceFields;
  steps?: { title: string; optional?: boolean }[];
  stepsCount?: Steps;
  stepsPortalId?: string;
  submitOnChange?: boolean;
}

export interface FormProps {
  actions?: (props: ActionsProps) => React.ReactElement;
  actionsComponent?: React.JSX.ElementType;
  actionsComponentProps?: unknown;
  fieldsArray: React.ReactNode[];
  formClassName?: string;
  formId?: string;
  formItemsClassName?: string;
  formState: FormState<any>;
  handleSubmit: UseFormHandleSubmit<object, FieldValues>;
  loading: boolean;
  loadingDesc?: string;
  isDirtyOverride?: boolean;
  onSubmitHandler: (newValues: Values) => void;
  renderAfterForm?: RenderAfterForm;
  sourceAppendValues?: SourceAppendValues;
  sourceFields?: SourceFields;
  stepIndex?: number;
  stepsCount?: Steps;
}

export type IsSubItemDraggableFn = (value: Value, index: number) => boolean;
export type IsSubItemRemovableFn = (value: Value, index: number) => boolean;
export type GetProtectedIndices = () => number[];

export type FieldArrayData = {
  append: UseFieldArrayAppend<any>;
  getProtectedIndices?: GetProtectedIndices;
  insert: UseFieldArrayInsert<any>;
  isSubItemDraggableFn?: IsSubItemDraggableFn;
  isSubItemRemovableFn?: IsSubItemRemovableFn;
  move: UseFieldArrayMove;
  remove: UseFieldArrayRemove;
  update: UseFieldArrayUpdate<any>;
  replace: UseFieldArrayReplace<any>;
};
export type FieldArrayDataObject = { [key: string]: FieldArrayData };
export type UpdateFieldArrayMethods = (
  parentName: string,
  data: FieldArrayData,
) => void;
export type RemoveFieldArrayMethods = (parentName: string) => void;
export type FieldArrayContextType = {
  updateFieldArrayMethods: UpdateFieldArrayMethods;
  removeFieldArrayMethods: RemoveFieldArrayMethods;
  fieldArrayMethodsByName: FieldArrayDataObject;
};

export type FieldArrayItemType = FieldArrayWithId<FieldValues, string, 'id'> & {
  position: number;
  values?: Value[];
  value?: Value;
};

export type AppendValue = { [key: string]: Value };

export type FieldArrayItemRenderAddMore = (
  appendValue?: AppendValue,
  appendCallback?: () => void,
) => React.ReactNode;

export type FieldArrayItemRenderEmptyState = (
  appendValue?: AppendValue,
  snapshot?: DroppableStateSnapshot,
  visible?: boolean,
) => React.ReactNode;
